import { z } from "zod";
import {
  ACCESS_TIERS,
  VIDEO_CATEGORIES,
  VIDEO_TAG_DIMENSIONS,
} from "../constants/index";
import { nonEmpty, uuid } from "./_primitives";

// A campaign_assigned-tier video without a campaign link can NEVER be played:
// the viewer gate requires a grant on the video's campaign, and a NULL
// campaignId short-circuits to "locked" (see view/tier.ts). Enforce the pair
// at create time; updates are re-checked route-side against the merged state.
const requireCampaignForTier = {
  check: (d: { accessTier?: string; campaignId?: string | null }) =>
    d.accessTier !== "campaign_assigned" || Boolean(d.campaignId),
  opts: {
    message: "A campaign is required when access tier is campaign_assigned",
    path: ["campaignId"] as ["campaignId"],
  },
};

const videoBaseSchema = z.object({
  campaignId: uuid.nullable().optional(),
  title: nonEmpty.max(200),
  description: z.string().optional(),
  category: z.enum(VIDEO_CATEGORIES),
  accessTier: z.enum(ACCESS_TIERS).default("registered"),
  // Audit V1: free-text supplemental tag (e.g. "Frylow") -- shown as a chip
  // in Library, used by Compose to filter quickly.
  categoryTag: z.string().max(80).nullable().optional(),
});

export const createVideoSchema = videoBaseSchema.refine(
  requireCampaignForTier.check,
  requireCampaignForTier.opts,
);

export const updateVideoSchema = videoBaseSchema.partial();

// Audit V1: create a YouTube-sourced video. Bypasses the Mux upload path
// entirely -- on save we mint a video + a video_version with source="youtube"
// and the youtubeVideoId extracted from the URL.
export const createYoutubeVideoSchema = videoBaseSchema
  .extend({ youtubeUrl: z.string().url() })
  .refine(requireCampaignForTier.check, requireCampaignForTier.opts)
  // CVO review #2 (P0): YouTube IFrame embeds sit outside Mux signed URLs and
  // capture no heartbeats/milestones — tier/NDA gating can't actually be
  // enforced on them (the underlying video is public on youtube.com). YouTube
  // content is therefore Public-tier only; gated tiers must use Mux uploads.
  .refine((d) => d.accessTier === "public", {
    message: "YouTube videos must be Public tier — gated tiers require a Mux upload",
    path: ["accessTier"],
  });

export type CreateYoutubeVideoInput = z.infer<typeof createYoutubeVideoSchema>;

export const tagSchema = z.object({
  dimension: z.enum(VIDEO_TAG_DIMENSIONS),
  value: nonEmpty.max(100),
});

export const setVideoTagsSchema = z.object({
  tags: z.array(tagSchema).max(50),
});

export const submitForApprovalSchema = z.object({
  note: z.string().optional(),
});

export const approveVideoSchema = z.object({
  comment: z.string().optional(),
});

export const rejectVideoSchema = z.object({
  comment: nonEmpty.max(2000), // required on reject
});

export const createMuxUploadSchema = z.object({
  videoId: uuid,
  note: z.string().optional(), // version note
});

export type CreateVideoInput = z.infer<typeof createVideoSchema>;
export type UpdateVideoInput = z.infer<typeof updateVideoSchema>;
export type SetVideoTagsInput = z.infer<typeof setVideoTagsSchema>;
export type SubmitForApprovalInput = z.infer<typeof submitForApprovalSchema>;
export type ApproveVideoInput = z.infer<typeof approveVideoSchema>;
export type RejectVideoInput = z.infer<typeof rejectVideoSchema>;
export type CreateMuxUploadInput = z.infer<typeof createMuxUploadSchema>;
